import React, { useEffect, useState } from "react";
import {
    FaWallet,
    FaArrowDown,
    FaArrowUp,
    FaQrcode,
    FaUpload,
    FaCheckCircle,
    FaClock,
    FaRupeeSign,
    FaTimesCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api";
import { useNavigate } from "react-router-dom";

const Credits = () => {
    // =====================================================
    // CREDIT SUMMARY STATE
    // =====================================================

    const [creditLimit, setCreditLimit] = useState(0);
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [availableCredit, setAvailableCredit] = useState(0);

    const navigate = useNavigate();

    // =====================================================
    // PAYMENT HISTORY STATE
    // =====================================================

    const [paymentHistory, setPaymentHistory] = useState([]);

    // =====================================================
    // PAYMENT FORM STATE
    // =====================================================

    const [selectedFile, setSelectedFile] = useState(null);
    const [transactionId, setTransactionId] = useState("");

    // =====================================================
    // LOADING STATES
    // =====================================================

    const [loadingSummary, setLoadingSummary] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [uploading, setUploading] = useState(false);

    const [paymentAmount, setPaymentAmount] = useState("");

    // =====================================================
    // FETCH CREDIT SUMMARY
    // GET /my-credit-summary
    // =====================================================

    const fetchCreditSummary = async () => {
        try {
            setLoadingSummary(true);

            const response = await api.get("/my-credit-summary");

            console.log("Credit Summary Response:", response.data);

            if (response.data.status) {
                setCreditLimit(Number(response.data.credit_limit || 0));

                setOutstandingAmount(
                    Number(response.data.outstanding || 0)
                );

                setAvailableCredit(
                    Number(response.data.available_credit || 0)
                );
            } else {
                toast.error(
                    response.data.message || "Unable to fetch credit details"
                );
            }
        } catch (error) {
            console.error("Credit Summary Error:", error);

            toast.error(
                error.response?.data?.message ||
                "Failed to fetch credit summary"
            );
        } finally {
            setLoadingSummary(false);
        }
    };

    // =====================================================
    // FETCH PAYMENT HISTORY
    // GET /credit-payment-history
    // =====================================================

    const fetchPaymentHistory = async () => {
        try {
            setLoadingHistory(true);

            const response = await api.get(
                "/credit-payment/history"
            );

            console.log("Payment History Response:", response.data);

            if (response.data.status) {
                setPaymentHistory(response.data.data || []);
            } else {
                toast.error(
                    response.data.message ||
                    "Unable to fetch payment history"
                );
            }
        } catch (error) {
            console.error("Payment History Error:", error);

            toast.error(
                error.response?.data?.message ||
                "Failed to fetch payment history"
            );
        } finally {
            setLoadingHistory(false);
        }
    };

    // =====================================================
    // FETCH DATA WHEN PAGE LOADS
    // =====================================================

    useEffect(() => {
        fetchCreditSummary();
        fetchPaymentHistory();
    }, []);

    // =====================================================
    // FILE SELECT
    // =====================================================

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (!file) {
            return;
        }

        // Only image because Laravel validation is:
        // payment_proof => required|image

        if (!file.type.startsWith("image/")) {
            toast.error("Please upload JPG, JPEG or PNG image only.");
            e.target.value = "";
            return;
        }

        // Optional 5MB limit

        if (file.size > 5 * 1024 * 1024) {
            toast.error("Payment proof image must be less than 5MB.");
            e.target.value = "";
            return;
        }

        setSelectedFile(file);
    };

    // =====================================================
    // UPLOAD PAYMENT PROOF
    // POST /credit-payment
    // =====================================================

    const handleUploadProof = async () => {
        // -----------------------------------------------
        // Validation
        // -----------------------------------------------

        if (!paymentAmount) {
            toast.error("Please enter payment amount.");
            return;
        }

        if (Number(paymentAmount) <= 0) {
            toast.error("Invalid amount.");
            return;
        }

        if (Number(paymentAmount) > Number(outstandingAmount)) {
            toast.error(
                `Amount cannot exceed outstanding ₹${outstandingAmount}`
            );
            return;
        }

        if (!transactionId.trim()) {
            toast.error("Please enter transaction ID.");
            return;
        }

        if (!selectedFile) {
            toast.error("Please select payment proof first.");
            return;
        }

        if (outstandingAmount <= 0) {
            toast.error("You have no outstanding amount.");
            return;
        }

        try {
            setUploading(true);

            // -----------------------------------------------
            // FormData
            // -----------------------------------------------

            const formData = new FormData();

            formData.append(
                "amount",
                paymentAmount
            );

            formData.append(
                "transaction_id",
                transactionId.trim()
            );

            formData.append(
                "payment_proof",
                selectedFile
            );

            // -----------------------------------------------
            // API CALL
            // -----------------------------------------------

            const response = await api.post(
                "/credit-payment/store",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            console.log(
                "Payment Upload Response:",
                response.data
            );

            // -----------------------------------------------
            // SUCCESS
            // -----------------------------------------------

            if (response.data.status) {
                toast.success(
                    response.data.message ||
                    "Payment proof submitted successfully."
                );

                // Reset form

                setPaymentAmount("");
                setSelectedFile(null);
                setTransactionId("");

                // Reset file input manually

                const fileInput =
                    document.getElementById("payment-proof");

                if (fileInput) {
                    fileInput.value = "";
                }

                // Refresh payment history

                fetchPaymentHistory();

                // Refresh credit summary

                fetchCreditSummary();
            } else {
                toast.error(
                    response.data.message ||
                    "Payment submission failed."
                );
            }

        } catch (error) {
            console.error(
                "Payment Upload Error:",
                error
            );

            console.log(
                "Server Error:",
                error.response?.data
            );

            // Laravel validation errors

            if (error.response?.data?.errors) {
                const errors =
                    error.response.data.errors;

                Object.values(errors).forEach(
                    (messages) => {
                        messages.forEach((message) => {
                            toast.error(message);
                        });
                    }
                );
            } else {
                toast.error(
                    error.response?.data?.message ||
                    "Failed to submit payment proof."
                );
            }

        } finally {
            setUploading(false);
        }
    };


    const handleRazorpayPayment = () => {

        if (!paymentAmount) {
            toast.error("Please enter payment amount");
            return;
        }

        if (Number(paymentAmount) <= 0) {
            toast.error("Invalid amount");
            return;
        }

        if (Number(paymentAmount) > Number(outstandingAmount)) {
            toast.error(
                `Amount cannot exceed outstanding ₹${outstandingAmount}`
            );
            return;
        }

        navigate("/razorpay", {
            state: {
                finalTotal: Number(paymentAmount),
                order_number: `CREDIT-${Date.now()}`,
            },
        });
    };

    // =====================================================
    // FORMAT DATE
    // =====================================================

    const formatDate = (date) => {
        if (!date) {
            return "-";
        }

        return new Date(date).toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        );
    };

    // =====================================================
    // FORMAT MONEY
    // =====================================================

    const formatMoney = (amount) => {
        return Number(amount || 0).toLocaleString(
            "en-IN"
        );
    };

    // =====================================================
    // PAYMENT STATUS UI
    // =====================================================

    const getStatusBadge = (status) => {
        const normalizedStatus =
            String(status || "").toLowerCase();

        if (normalizedStatus === "approved") {
            return (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-[11px] font-semibold">
                    <FaCheckCircle size={10} />
                    Approved
                </span>
            );
        }

        if (normalizedStatus === "rejected") {
            return (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-[11px] font-semibold">
                    <FaTimesCircle size={10} />
                    Rejected
                </span>
            );
        }

        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-50 text-yellow-700 text-[11px] font-semibold">
                <FaClock size={10} />
                Pending
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-gray-100 py-6 px-4 sm:px-6 lg:px-8">

            <div className="max-w-7xl mx-auto">

                {/* =====================================================
            HEADER
        ===================================================== */}

                <div className="mb-5">

                    <div className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg shadow">

                        <span className="text-xs sm:text-sm font-semibold">
                            MY CREDIT
                        </span>

                    </div>

                </div>


                {/* =====================================================
            CREDIT SUMMARY CARDS
        ===================================================== */}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">

                    {/* CREDIT LIMIT */}

                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">

                        <div className="flex items-center gap-3">

                            <div className="w-11 h-11 rounded-lg bg-yellow-50 flex items-center justify-center">

                                <FaWallet className="text-yellow-600 text-lg" />

                            </div>

                            <div>

                                <p className="text-xs text-gray-500 font-medium">
                                    Credit Limit
                                </p>

                                <p className="text-lg font-bold text-black">

                                    {loadingSummary
                                        ? "Loading..."
                                        : `₹ ${formatMoney(creditLimit)}`}

                                </p>

                            </div>

                        </div>

                    </div>


                    {/* OUTSTANDING */}

                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">

                        <div className="flex items-center gap-3">

                            <div className="w-11 h-11 rounded-lg bg-orange-50 flex items-center justify-center">

                                <FaArrowDown className="text-orange-500 text-lg" />

                            </div>

                            <div>

                                <p className="text-xs text-gray-500 font-medium">
                                    Outstanding
                                </p>

                                <p className="text-lg font-bold text-orange-600">

                                    {loadingSummary
                                        ? "Loading..."
                                        : `₹ ${formatMoney(outstandingAmount)}`}

                                </p>

                            </div>

                        </div>

                    </div>


                    {/* AVAILABLE CREDIT */}

                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">

                        <div className="flex items-center gap-3">

                            <div className="w-11 h-11 rounded-lg bg-green-50 flex items-center justify-center">

                                <FaArrowUp className="text-green-600 text-lg" />

                            </div>

                            <div>

                                <p className="text-xs text-gray-500 font-medium">
                                    Available Credit
                                </p>

                                <p className="text-lg font-bold text-green-600">

                                    {loadingSummary
                                        ? "Loading..."
                                        : `₹ ${formatMoney(availableCredit)}`}

                                </p>

                            </div>

                        </div>

                    </div>


                    {/* PAY OUTSTANDING */}

                    <div className="bg-black rounded-xl shadow-lg p-4 flex flex-col justify-between">

                        <div className="flex items-center justify-between">

                            <div>

                                <p className="text-xs text-gray-300 font-medium">
                                    Outstanding Amount
                                </p>

                                <p className="text-xl font-bold text-yellow-400 mt-1">

                                    ₹ {formatMoney(outstandingAmount)}

                                </p>

                            </div>

                            {/* <FaRupeeSign className="text-yellow-500 text-2xl" /> */}

                        </div>

                        <button
                            onClick={() =>
                                document
                                    .getElementById("pay-outstanding")
                                    ?.scrollIntoView({
                                        behavior: "smooth",
                                    })
                            }
                            className="mt-3 w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm py-2 rounded-lg transition"
                        >
                            Pay Outstanding
                        </button>

                    </div>

                </div>


                {/* =====================================================
                    MAIN CONTENT
                    ===================================================== 
                */}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">


                    {/* =====================================================
              PAYMENT HISTORY
          ===================================================== */}

                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">

                            <div>

                                <h2 className="text-base font-bold text-black">
                                    Payment History
                                </h2>

                                <p className="text-xs text-gray-500 mt-1">
                                    Your recent credit payments
                                </p>

                            </div>

                            <FaWallet className="text-yellow-600" />

                        </div>


                        {/* Desktop Table */}

                        <div className="hidden sm:block overflow-x-auto">

                            <table className="w-full">

                                <thead className="bg-gray-50">

                                    <tr>

                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                                            Date
                                        </th>

                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                                            Amount
                                        </th>

                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                                            Type
                                        </th>

                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                                            Status
                                        </th>

                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                                            Transaction ID
                                        </th>

                                    </tr>

                                </thead>


                                <tbody className="divide-y divide-gray-100">

                                    {loadingHistory ? (

                                        <tr>

                                            <td
                                                colSpan="5"
                                                className="px-4 py-8 text-center text-sm text-gray-500"
                                            >
                                                Loading payment history...
                                            </td>

                                        </tr>

                                    ) : paymentHistory.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan="5"
                                                className="px-4 py-8 text-center text-sm text-gray-500"
                                            >
                                                No payment history found.
                                            </td>

                                        </tr>

                                    ) : (

                                        paymentHistory.map((payment) => (

                                            <tr
                                                key={payment.id}
                                                className="hover:bg-yellow-50/40 transition"
                                            >

                                                <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">

                                                    {formatDate(
                                                        payment.created_at
                                                    )}

                                                </td>


                                                <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">

                                                    ₹{formatMoney(payment.amount)}

                                                </td>


                                                <td className="px-4 py-3 text-xs text-gray-600">

                                                    Payment

                                                </td>


                                                <td className="px-4 py-3">

                                                    {getStatusBadge(
                                                        payment.status
                                                    )}

                                                </td>


                                                <td className="px-4 py-3 text-[11px] text-gray-500 break-all">

                                                    {payment.transaction_id || "-"}

                                                </td>

                                            </tr>

                                        ))

                                    )}

                                </tbody>

                            </table>

                        </div>


                        {/* Mobile Payment Cards */}

                        <div className="sm:hidden divide-y divide-gray-100">

                            {loadingHistory ? (

                                <div className="p-6 text-center text-sm text-gray-500">
                                    Loading payment history...
                                </div>

                            ) : paymentHistory.length === 0 ? (

                                <div className="p-6 text-center text-sm text-gray-500">
                                    No payment history found.
                                </div>

                            ) : (

                                paymentHistory.map((payment) => (

                                    <div
                                        key={payment.id}
                                        className="p-4"
                                    >

                                        <div className="flex justify-between items-start">

                                            <div>

                                                <p className="text-xs text-gray-500">

                                                    {formatDate(
                                                        payment.created_at
                                                    )}

                                                </p>

                                                <p className="text-base font-bold text-gray-900 mt-1">

                                                    ₹{formatMoney(payment.amount)}

                                                </p>

                                            </div>

                                            {getStatusBadge(
                                                payment.status
                                            )}

                                        </div>


                                        <div className="mt-3 text-xs text-gray-500">

                                            Payment

                                        </div>


                                        <div className="mt-1 text-[10px] text-gray-400 break-all">

                                            Transaction ID:{" "}

                                            {payment.transaction_id || "-"}

                                        </div>

                                    </div>

                                ))

                            )}

                        </div>


                        {/* View All */}

                        <div className="border-t border-gray-100 px-5 py-3">

                            <button
                                onClick={fetchPaymentHistory}
                                className="text-xs font-semibold text-yellow-700 hover:text-black transition"
                            >
                                Refresh History →
                            </button>

                        </div>

                    </div>


                    {/* =====================================================
                        PAY OUTSTANDING
                    ===================================================== */}

                    <div
                        id="pay-outstanding"
                        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                    >

                        <div className="px-5 py-4 border-b border-gray-100">

                            <h2 className="text-base font-bold text-black">
                                Pay Outstanding
                            </h2>

                            <p className="text-xs text-gray-500 mt-1">
                                Pay your outstanding amount and submit the
                                payment proof for verification.
                            </p>

                        </div>


                        <div className="p-5">

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


                                {/* =================================================
                                        QR CODE
                                    ================================================= */}

                                <div className="flex flex-col items-center justify-center">

                                    <div className="w-full bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">

                                        <FaWallet
                                            className="mx-auto text-yellow-600 mb-3"
                                            size={45}
                                        />

                                        <h3 className="font-bold text-lg text-black">
                                            Pay via Razorpay
                                        </h3>

                                        <p className="text-xs text-gray-500 mt-2">
                                            Secure payment using UPI, Cards,
                                            Net Banking and Wallets
                                        </p>

                                        <button
                                            onClick={handleRazorpayPayment}
                                            className="
                                                mt-5
                                                w-full
                                                bg-black
                                                hover:bg-gray-900
                                                text-yellow-400
                                                border
                                                border-yellow-500
                                                font-semibold
                                                py-3
                                                rounded-xl
                                            "
                                        >
                                            Pay ₹{formatMoney(paymentAmount || 0)}
                                        </button>

                                    </div>

                                </div>


                                {/* =================================================
                                    PAYMENT INSTRUCTIONS
                                ================================================= */}

                                <div>

                                    {/* PAYMENT AMOUNT */}

                                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">

                                        <p className="text-xs text-gray-600 font-medium">
                                            Payment Amount
                                        </p>

                                        <p className="text-2xl font-bold text-black mt-1">

                                            ₹{formatMoney(outstandingAmount)}

                                        </p>

                                    </div>


                                    <h3 className="text-sm font-bold text-gray-800 mb-3">
                                        Payment Process
                                    </h3>


                                    <div className="space-y-3">

                                        <div className="flex gap-3">

                                            <div className="w-6 h-6 rounded-full bg-black text-yellow-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                1
                                            </div>

                                            <p className="text-xs text-gray-600">
                                                Make the payment using the above UPI ID
                                                or QR code.
                                            </p>

                                        </div>


                                        <div className="flex gap-3">

                                            <div className="w-6 h-6 rounded-full bg-black text-yellow-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                2
                                            </div>

                                            <p className="text-xs text-gray-600">
                                                Take a screenshot after completing
                                                the payment.
                                            </p>

                                        </div>


                                        <div className="flex gap-3">

                                            <div className="w-6 h-6 rounded-full bg-black text-yellow-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                3
                                            </div>

                                            <p className="text-xs text-gray-600">
                                                Enter your transaction ID and upload
                                                the payment screenshot.
                                            </p>

                                        </div>

                                    </div>


                                    {/* =================================================
                      TRANSACTION Amount
                  ================================================= */}
                                    <div className="mt-5">

                                        <label className="block text-xs font-semibold text-gray-700 mb-2">
                                            Payment Amount
                                        </label>

                                        <input
                                            type="number"
                                            min="1"
                                            max={outstandingAmount}
                                            value={paymentAmount}
                                            onChange={(e) => setPaymentAmount(e.target.value)}
                                            placeholder="Enter amount you paid"
                                            className="
                                              w-full
                                              border
                                              border-gray-300
                                              rounded-xl
                                              px-4
                                              py-3
                                              text-sm
                                              outline-none
                                              focus:border-yellow-500
                                              focus:ring-1
                                              focus:ring-yellow-500
                                            "
                                        />

                                        <p className="text-xs text-gray-500 mt-1">
                                            Outstanding Amount: ₹{formatMoney(outstandingAmount)}
                                        </p>

                                    </div>

                                    {/* =================================================
                      TRANSACTION ID
                  ================================================= */}

                                    <div className="mt-5">

                                        <label className="block text-xs font-semibold text-gray-700 mb-2">

                                            Transaction ID

                                        </label>

                                        <input
                                            type="text"
                                            value={transactionId}
                                            onChange={(e) =>
                                                setTransactionId(
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Enter UPI transaction ID"
                                            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                                        />

                                    </div>


                                    {/* =================================================
                      UPLOAD PAYMENT PROOF
                  ================================================= */}

                                    <div className="mt-4">

                                        <label
                                            htmlFor="payment-proof"
                                            className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 hover:border-yellow-500 rounded-xl px-4 py-4 cursor-pointer transition"
                                        >

                                            <FaUpload className="text-yellow-600" />

                                            <div className="text-center">

                                                <p className="text-xs font-semibold text-gray-700 break-all">

                                                    {selectedFile
                                                        ? selectedFile.name
                                                        : "Upload Payment Proof"}

                                                </p>

                                                {!selectedFile && (

                                                    <p className="text-[10px] text-gray-400 mt-1">
                                                        JPG, JPEG or PNG — Max 5MB
                                                    </p>

                                                )}

                                            </div>

                                        </label>


                                        <input
                                            id="payment-proof"
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />


                                        {/* SUBMIT BUTTON */}

                                        <button
                                            onClick={handleUploadProof}
                                            disabled={uploading || outstandingAmount <= 0}
                                            className={`w-full mt-3 font-semibold text-sm py-2.5 rounded-xl transition flex items-center justify-center gap-2
                        ${uploading ||
                                                    outstandingAmount <= 0
                                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                    : "bg-black hover:bg-gray-900 text-yellow-400 border border-yellow-500"
                                                }
                      `}
                                        >

                                            {uploading ? (

                                                <>
                                                    <span className="animate-spin h-4 w-4 border-2 border-yellow-400 border-t-transparent rounded-full"></span>

                                                    Submitting...

                                                </>

                                            ) : (

                                                <>
                                                    <FaUpload size={13} />

                                                    Submit Payment Proof

                                                </>

                                            )}

                                        </button>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>


                {/* =====================================================
            CREDIT INFORMATION
        ===================================================== */}

                <div className="mt-5 bg-black rounded-xl p-5 shadow-lg">

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                        <div>

                            <h3 className="text-sm font-bold text-yellow-400">
                                Credit Account Information
                            </h3>

                            <p className="text-xs text-gray-400 mt-1">
                                Your available credit is automatically updated
                                after payment approval.
                            </p>

                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-300">

                            <FaClock className="text-yellow-500" />

                            Payment verification may take some time.

                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
};

export default Credits;